if (!customElements.get('moonou-image-gallery')) {
  customElements.define('moonou-image-gallery', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.moonou-image-gallery__track');
      this.dots = [...this.querySelectorAll('[data-gallery-index]')];
      this.arrows = [...this.querySelectorAll('[data-gallery-step]')];
      if (!this.track || this.dots.length < 2) return;
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.addEventListener('click', (event) => {
        const dot = event.target.closest('[data-gallery-index]');
        if (dot) this.select(Number(dot.dataset.galleryIndex));
        const arrow = event.target.closest('[data-gallery-step]');
        if (arrow && arrow.getAttribute('aria-disabled') !== 'true') {
          this.select(this.index + Number(arrow.dataset.galleryStep));
        }
      }, options);
      this.track.addEventListener('scroll', () => this.updateDots(), { ...options, passive: true });
      this.track.addEventListener('keydown', (event) => {
        const keys = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.dots.length - 1 };
        if (!(event.key in keys)) return;
        event.preventDefault();
        event.stopPropagation();
        this.select(keys[event.key]);
      }, options);
      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        this.drag = { x: event.clientX, left: this.track.scrollLeft, id: event.pointerId, moved: false };
      }, options);
      this.track.addEventListener('pointermove', (event) => {
        if (!this.drag) return;
        const distance = event.clientX - this.drag.x;
        if (!this.drag.moved && Math.abs(distance) < 6) return;
        if (!this.drag.moved) {
          this.drag.moved = true;
          this.track.setPointerCapture(event.pointerId);
          this.track.style.scrollSnapType = 'none';
          this.track.classList.add('is-dragging');
        }
        this.track.scrollLeft = this.drag.left - distance;
      }, options);
      const stopDrag = () => {
        if (!this.drag) return;
        const { id, moved } = this.drag;
        this.drag = null;
        if (this.track.hasPointerCapture(id)) this.track.releasePointerCapture(id);
        this.track.style.removeProperty('scroll-snap-type');
        this.track.classList.remove('is-dragging');
        if (moved) this.select(this.index);
      };
      this.track.addEventListener('pointerup', stopDrag, options);
      this.track.addEventListener('pointercancel', stopDrag, options);
      this.track.addEventListener('lostpointercapture', stopDrag, options);
      this.track.addEventListener('pointerleave', () => {
        if (this.drag && !this.drag.moved) this.drag = null;
      }, options);
      this.resizeObserver = new ResizeObserver(() => {
        if (this.track.clientWidth) this.select(this.selectedIndex || 0, true);
      });
      this.resizeObserver.observe(this.track);
      this.updateDots();
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.resizeObserver?.disconnect();
    }

    get index() {
      return this.track.clientWidth ? Math.round(this.track.scrollLeft / this.track.clientWidth) : 0;
    }

    select(index, immediate = false) {
      const target = Math.max(0, Math.min(this.dots.length - 1, index));
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: target * this.track.clientWidth, behavior: immediate || reducedMotion ? 'instant' : 'smooth' });
    }

    updateDots() {
      this.selectedIndex = this.index;
      this.dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === this.selectedIndex)));
      this.arrows.forEach((arrow) => {
        const atEdge = Number(arrow.dataset.galleryStep) < 0
          ? this.selectedIndex === 0
          : this.selectedIndex === this.dots.length - 1;
        arrow.setAttribute('aria-disabled', String(atEdge));
      });
    }
  });
}
